/**
 * My Reminders Service
 *
 * Service layer for interacting with the My Reminders state.
 * Provides methods for loading and managing reminders for students in the current user's caseload from Firestore.
 *
 * Reminders documents are stored at: partners/{partnerId}/reminders/{reminderId}
 *
 * Real-time sync is maintained to keep the local state up to date with Firestore changes.
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { MyRemindersState, MyRemindersStateModel } from './my-reminders.state';
import {
  Initialize,
  SetReminders,
  SetInitialized,
  AddReminder,
  AddRemindersBatch,
  UpdateReminder,
  DeleteReminder,
  DeleteRemindersByGoal,
  ClearMyReminders,
} from './my-reminders.actions';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { Reminder } from 'src/app/models/schema';
import { ActiveTeamState } from '../../account/active-team';
import { AuthState } from '../../../store/auth';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { UtilsService } from 'src/app/services/utils.service';
import { Unsubscribe } from '@angular/fire/firestore';

/**
 * My Reminders Service
 *
 * Handles all my-reminders-related operations including loading from
 * and saving to Firestore with real-time sync support.
 */
@Injectable({
  providedIn: 'root',
})
export class MyRemindersService implements OnDestroy {
  private store: Store = inject(Store);
  private firestoreService: FirestoreService = inject(FirestoreService);
  private utilsService: UtilsService = inject(UtilsService);

  /**
   * Active reminders listener unsubscribe function
   */
  private remindersUnsubscribe?: Unsubscribe;

  /**
   * Profile caseload subscription
   */
  private caseloadSubscription?: Subscription;

  /**
   * Cache of all reminders loaded from Firestore (before filtering)
   */
  private allRemindersCache: Reminder[] = [];

  // ========================================
  // Observable Selectors
  // ========================================

  /**
   * Observable of the entire my reminders state
   */
  @Select(MyRemindersState.getState)
  myRemindersState$!: Observable<MyRemindersStateModel>;

  /**
   * Observable of all reminders
   */
  @Select(MyRemindersState.reminders)
  reminders$!: Observable<Reminder[]>;

  /**
   * Observable of reminder IDs
   */
  @Select(MyRemindersState.reminderIds)
  reminderIds$!: Observable<string[]>;

  /**
   * Observable of reminder count
   */
  @Select(MyRemindersState.reminderCount)
  reminderCount$!: Observable<number>;

  /**
   * Observable of initialized state
   */
  @Select(MyRemindersState.initialized)
  initialized$!: Observable<boolean>;

  /**
   * Observable of whether there are reminders
   */
  @Select(MyRemindersState.hasReminders)
  hasReminders$!: Observable<boolean>;

  /**
   * Observable of upcoming reminders
   */
  @Select(MyRemindersState.upcomingReminders)
  upcomingReminders$!: Observable<Reminder[]>;

  /**
   * Observable of overdue reminders
   */
  @Select(MyRemindersState.overdueReminders)
  overdueReminders$!: Observable<Reminder[]>;

  /**
   * Observable of reminders sorted by date
   */
  @Select(MyRemindersState.remindersSortedByDate)
  remindersSortedByDate$!: Observable<Reminder[]>;

  // ========================================
  // Synchronous Getters
  // ========================================

  /**
   * Get all reminders (synchronous)
   */
  get reminders(): Reminder[] {
    return this.store.selectSnapshot(MyRemindersState.reminders);
  }

  /**
   * Get reminder IDs (synchronous)
   */
  get reminderIds(): string[] {
    return this.store.selectSnapshot(MyRemindersState.reminderIds);
  }

  /**
   * Get reminder count (synchronous)
   */
  get reminderCount(): number {
    return this.store.selectSnapshot(MyRemindersState.reminderCount);
  }

  /**
   * Get initialized state (synchronous)
   */
  get isInitialized(): boolean {
    return this.store.selectSnapshot(MyRemindersState.initialized);
  }

  /**
   * Check if there are reminders (synchronous)
   */
  get hasReminders(): boolean {
    return this.store.selectSnapshot(MyRemindersState.hasReminders);
  }

  /**
   * Get upcoming reminders (synchronous)
   */
  get upcomingReminders(): Reminder[] {
    return this.store.selectSnapshot(MyRemindersState.upcomingReminders);
  }

  /**
   * Get overdue reminders (synchronous)
   */
  get overdueReminders(): Reminder[] {
    return this.store.selectSnapshot(MyRemindersState.overdueReminders);
  }

  /**
   * Get a specific reminder by ID (synchronous)
   */
  getReminder(id: string): Reminder | null {
    return this.store.selectSnapshot(MyRemindersState.reminder(id));
  }

  /**
   * Get reminders by goal ID (synchronous)
   */
  getRemindersByGoalId(goalId: string): Reminder[] {
    return this.store.selectSnapshot(MyRemindersState.remindersByGoalId(goalId));
  }

  /**
   * Get reminders by student ID (synchronous)
   */
  getRemindersByStudentId(studentId: string): Reminder[] {
    return this.store.selectSnapshot(MyRemindersState.remindersByStudentId(studentId));
  }

  // ========================================
  // Helper Methods
  // ========================================

  /**
   * Convert undefined values to null for Firestore compatibility
   * Firestore doesn't support undefined values, so we convert them to null
   */
  private convertUndefinedToNull<T extends Record<string, any>>(obj: T): T {
    const result: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value === undefined) {
          result[key] = null;
        } else if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          // Recursively handle nested objects
          result[key] = this.convertUndefinedToNull(value);
        } else {
          result[key] = value;
        }
      }
    }
    return result as T;
  }

  // ========================================
  // Service Methods
  // ========================================

  /**
   * Initialize my reminders service
   *
   * Loads reminders for students in the active user's caseload and sets up real-time sync.
   * If the user has no caseload or an empty caseload, sets initialized to true
   * with an empty reminders array.
   * 
   * Subscribes to both:
   * 1. Reminders collection changes (real-time updates to reminder data)
   * 2. Profile caseload changes (real-time updates to which reminders to show)
   */
  async initialize(): Promise<void> {
    console.log('[MyRemindersService] Initializing...');

    // Dispatch initialize action
    this.store.dispatch(new Initialize());

    // Clean up existing listeners if any
    if (this.remindersUnsubscribe) {
      this.remindersUnsubscribe();
      this.remindersUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }

    // Get active team
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      console.warn('[MyRemindersService] No active team, setting initialized with empty reminders');
      this.store.dispatch(new SetReminders([]));
      this.store.dispatch(new SetInitialized(true));
      return;
    }

    // Subscribe to profile caseload changes for real-time filtering
    this.caseloadSubscription = this.store
      .select(ProfileState.profile)
      .pipe(distinctUntilChanged((prev, curr) => {
        // Only re-filter if caseload actually changed
        const prevCaseload = prev?.caseload || [];
        const currCaseload = curr?.caseload || [];
        return JSON.stringify(prevCaseload.sort()) === JSON.stringify(currCaseload.sort());
      }))
      .subscribe((profile) => {
        const caseload = profile?.caseload || [];
        console.log(`[MyRemindersService] Caseload updated: ${caseload.length} students`);
        
        // Re-filter cached reminders with new caseload
        this.filterAndUpdateReminders(caseload);
      });

    // Get initial caseload
    const profile = this.store.selectSnapshot(ProfileState.profile);
    const initialCaseload = profile?.caseload || [];

    // If no caseload or empty caseload, set initialized with empty reminders
    if (!initialCaseload || initialCaseload.length === 0) {
      console.log('[MyRemindersService] No caseload found, setting initialized with empty reminders');
      this.store.dispatch(new SetReminders([]));
      this.store.dispatch(new SetInitialized(true));
      // Still set up reminders listener so we can react if caseload gets populated later
    }

    return new Promise((resolve, reject) => {
      let isInitialLoad = true;

      try {
        // Collection path
        const collectionPath = `partners/${activeTeamId}/reminders`;

        console.log(`[MyRemindersService] Setting up real-time listener for reminders`);

        // Set up real-time listener for all reminders
        this.remindersUnsubscribe =
          this.firestoreService.onCollectionChange<Reminder>(
            collectionPath,
            (allReminders) => {
              // Cache all reminders
              this.allRemindersCache = allReminders;
              
              // Get current caseload from state
              const currentProfile = this.store.selectSnapshot(ProfileState.profile);
              const currentCaseload = currentProfile?.caseload || [];

              // Filter reminders by current caseload
              this.filterAndUpdateReminders(currentCaseload);

              console.log(
                `[MyRemindersService] Loaded ${allReminders.length} total reminders, filtered to caseload`
              );

              // Resolve promise on initial load
              if (isInitialLoad) {
                isInitialLoad = false;
                resolve();
              }
            },
            {} // No query options - load all reminders
          );
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to load my reminders';
        console.error(
          '[MyRemindersService] Error loading my reminders:',
          errorMessage
        );
        // Still set initialized even on error
        this.store.dispatch(new SetReminders([]));
        this.store.dispatch(new SetInitialized(true));
        reject(error);
      }
    });
  }

  /**
   * Filter cached reminders by caseload and update state
   * 
   * Reminders are filtered by checking if reminder.studentId is in the caseload array
   * 
   * @param caseload - Array of student IDs to include
   */
  private filterAndUpdateReminders(caseload: string[]): void {
    // Filter cached reminders by caseload student IDs (client-side filtering)
    const filteredReminders = this.allRemindersCache.filter((reminder) => {
      return reminder.studentId && caseload.includes(reminder.studentId);
    });

    // Dispatch success action
    this.store.dispatch(new SetReminders(filteredReminders));
    this.store.dispatch(new SetInitialized(true));

    console.log(
      `[MyRemindersService] Filtered to ${filteredReminders.length} reminders for ${caseload.length} students in caseload`
    );
  }

  /**
   * Add multiple reminders to Firestore (batch operation)
   * Used during goal creation to add all reminders at once
   * 
   * @param reminders - Array of reminders to add
   * @param partnerId - Partner ID for collection path
   */
  async addRemindersBatch(reminders: Reminder[], partnerId: string): Promise<void> {
    if (!reminders || reminders.length === 0) {
      console.warn('[MyRemindersService] No reminders to add');
      return;
    }

    console.log(`[MyRemindersService] Adding ${reminders.length} reminders in batch`);

    // Optimistically add to local state
    this.store.dispatch(new AddRemindersBatch(reminders));

    try {
      // Use Firestore batch write for efficiency
      const batch = this.firestoreService.createBatch();
      const collectionPath = `partners/${partnerId}/reminders`;

      reminders.forEach((reminder) => {
        const docRef = this.firestoreService.getDocumentReference(
          `${collectionPath}/${reminder.id}`
        );
        const cleanedReminder = this.convertUndefinedToNull(reminder);
        batch.set(docRef, cleanedReminder);
      });

      // Commit batch
      await batch.commit();

      console.log(`[MyRemindersService] Successfully added ${reminders.length} reminders`);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to add reminders';
      console.error('[MyRemindersService] Error adding reminders:', errorMessage);
      throw error;
    }
  }

  /**
   * Update a reminder in Firestore
   *
   * @param reminderId - ID of the reminder to update
   * @param updates - Partial reminder data to update
   * @param partnerId - Partner ID for collection path
   */
  async updateReminder(
    reminderId: string,
    updates: Partial<Reminder>,
    partnerId: string
  ): Promise<void> {
    // Optimistically update local state
    this.store.dispatch(new UpdateReminder(reminderId, updates));

    try {
      const documentPath = `partners/${partnerId}/reminders/${reminderId}`;
      const cleanedUpdates = this.convertUndefinedToNull(updates);
      
      await this.firestoreService.updateDocumentAsync(
        documentPath,
        cleanedUpdates
      );

      console.log(`[MyRemindersService] Successfully updated reminder ${reminderId}`);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to update reminder';
      console.error(
        '[MyRemindersService] Error updating reminder:',
        errorMessage
      );
      throw error;
    }
  }

  /**
   * Delete a reminder from Firestore
   *
   * @param reminderId - ID of the reminder to delete
   * @param partnerId - Partner ID for collection path
   */
  async deleteReminder(reminderId: string, partnerId: string): Promise<void> {
    // Optimistically delete from local state
    this.store.dispatch(new DeleteReminder(reminderId));

    try {
      const documentPath = `partners/${partnerId}/reminders/${reminderId}`;
      await this.firestoreService.deleteDocumentAsync(documentPath);

      console.log(`[MyRemindersService] Successfully deleted reminder ${reminderId}`);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete reminder';
      console.error(
        '[MyRemindersService] Error deleting reminder:',
        errorMessage
      );
      throw error;
    }
  }

  /**
   * Delete all reminders for a specific goal
   * Used when a goal is deleted
   *
   * @param goalId - ID of the goal whose reminders to delete
   * @param partnerId - Partner ID for collection path
   */
  async deleteRemindersByGoal(goalId: string, partnerId: string): Promise<void> {
    const remindersToDelete = this.getRemindersByGoalId(goalId);
    
    if (remindersToDelete.length === 0) {
      console.log(`[MyRemindersService] No reminders found for goal ${goalId}`);
      return;
    }

    console.log(`[MyRemindersService] Deleting ${remindersToDelete.length} reminders for goal ${goalId}`);

    // Optimistically delete from local state
    this.store.dispatch(new DeleteRemindersByGoal(goalId));

    try {
      // Use batch delete for efficiency
      const batch = this.firestoreService.createBatch();
      const collectionPath = `partners/${partnerId}/reminders`;

      remindersToDelete.forEach((reminder) => {
        const docRef = this.firestoreService.getDocumentReference(
          `${collectionPath}/${reminder.id}`
        );
        batch.delete(docRef);
      });

      // Commit batch
      await batch.commit();

      console.log(`[MyRemindersService] Successfully deleted ${remindersToDelete.length} reminders`);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete reminders';
      console.error(
        '[MyRemindersService] Error deleting reminders:',
        errorMessage
      );
      throw error;
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    if (this.remindersUnsubscribe) {
      this.remindersUnsubscribe();
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
    }
  }
}
